import React, { useState, useEffect } from 'react';
import { statisticsService } from '../../../services/backoffice/statisticsService';

interface Statistics {
  total_users: number;
  active_users: number;
  blocked_users: number;
  trial_users: number;
  paid_users: number;
  expired_users: number;
  total_calculations: number;
  calculations_today: number;
  total_revenue: number;
  revenue_this_month: number;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await statisticsService.getStatistics();
      setStats(data);
    } catch (err) {
      setError('שגיאה בטעינת הסטטיסטיקות');
      console.error('Error loading statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <span className="text-red-500 ml-2">⚠️</span>
          <span className="text-red-700">{error}</span>
        </div>
        <button 
          onClick={loadStatistics}
          className="mt-2 text-red-600 hover:text-red-800 text-sm underline"
        >
          נסה שוב
        </button>
      </div>
    );
  }

  const statCards = [
    {
      title: 'סה"כ משתמשים',
      value: stats?.total_users || 0,
      icon: '👥',
      color: 'bg-blue-500',
      change: '+' + (stats?.active_users || 0) + ' פעילים'
    },
    {
      title: 'משתמשים בתקופת נסיון',
      value: stats?.trial_users || 0,
      icon: '🎯',
      color: 'bg-yellow-500',
      change: (stats?.blocked_users || 0) + ' חסומים'
    },
    {
      title: 'משתמשים בתשלום',
      value: stats?.paid_users || 0,
      icon: '💳',
      color: 'bg-green-500',
      change: (stats?.expired_users || 0) + ' פגי תוקף'
    },
    {
      title: 'חישובים היום',
      value: stats?.calculations_today || 0,
      icon: '📊',
      color: 'bg-purple-500',
      change: 'מתוך ' + (stats?.total_calculations || 0) + ' סה"כ'
    },
    {
      title: 'הכנסות החודש',
      value: '₪' + (stats?.revenue_this_month || 0).toLocaleString(),
      icon: '💰',
      color: 'bg-indigo-500',
      change: 'מתוך ₪' + (stats?.total_revenue || 0).toLocaleString() + ' סה"כ'
    }
  ];

  return (
    <div className="space-y-6">
      {/* כרטיסי סטטיסטיקות */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {statCards.map((card, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{card.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{card.value}</p>
                <p className="text-sm text-gray-500 mt-1">{card.change}</p>
              </div>
              <div className={`w-12 h-12 rounded-full ${card.color} flex items-center justify-center text-white text-2xl`}>
                {card.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* סקירה מהירה */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">סטטוס משתמשים</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">פעילים</span>
              <span className="font-medium text-green-600">{stats?.active_users || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">בתקופת נסיון</span>
              <span className="font-medium text-yellow-600">{stats?.trial_users || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">חסומים</span>
              <span className="font-medium text-red-600">{stats?.blocked_users || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">פגי תוקף</span>
              <span className="font-medium text-gray-600">{stats?.expired_users || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">פעילות מערכת</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">חישובים היום</span>
              <span className="font-medium text-blue-600">{stats?.calculations_today || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">סה"כ חישובים</span>
              <span className="font-medium text-gray-600">{stats?.total_calculations || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">הכנסות החודש</span>
              <span className="font-medium text-green-600">₪{(stats?.revenue_this_month || 0).toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">סה"כ הכנסות</span>
              <span className="font-medium text-gray-600">₪{(stats?.total_revenue || 0).toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* כפתור רענון */}
      <div className="flex justify-end">
        <button
          onClick={loadStatistics}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
        >
          רענן נתונים
        </button>
      </div>
    </div>
  );
};

export default Dashboard;